import { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Chip,
  Box,
  Typography,
  Collapse,
  IconButton
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  ExpandMore,
  ExpandLess,
  LocationOn
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface AreaMatch {
  location_id?: string;
  region_id: string;
  chinese: string;
  english: string;
  region_chinese: string;
  region_english: string;
  [key: string]: unknown;
}

export interface AreaSuggestionItem {
  _id: string;
  region_id: string;
  chinese: string;
  english: string;
  region_chinese: string;
  region_english: string;
  score: number;
  [key: string]: unknown;
}

export interface AreaSuggestionMatchResult {
  match: AreaMatch | null;
  confidence: number;
  suggestions: AreaSuggestionItem[];
  debugInfo?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface AcceptSuggestionPayload {
  regionId: string;
  locationId: string;
  match: AreaMatch | AreaSuggestionItem;
}

export interface AreaSuggestionProps {
  matchResult: AreaSuggestionMatchResult | null;
  onAcceptSuggestion: (payload: AcceptSuggestionPayload) => void;
  onDismiss?: () => void;
  currentSelection?: string;
  showDebugInfo?: boolean;
}

const AreaSuggestion: React.FC<AreaSuggestionProps> = ({
  matchResult,
  onAcceptSuggestion,
  onDismiss,
  currentSelection,
  showDebugInfo = false
}) => {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Reset dismissed state when new match result comes in
    if (matchResult?.match) {
      setIsDismissed(false);
    }
  }, [matchResult]);

  if (!matchResult?.match || matchResult.confidence < 30 || isDismissed) {
    return null;
  }

  const { match, confidence, suggestions } = matchResult;
  const matchLocationId = match.location_id || (match as AreaSuggestionItem)._id || '';
  const isCurrentSelection = currentSelection === matchLocationId;

  const handleAccept = () => {
    onAcceptSuggestion({
      regionId: match.region_id,
      locationId: matchLocationId,
      match: match
    });
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  const getConfidenceColor = (conf: number): 'success' | 'warning' | 'info' => {
    if (conf >= 70) return 'success';
    if (conf >= 50) return 'warning';
    return 'info';
  };

  const getConfidenceText = (conf: number): string => {
    if (conf >= 70) return t('areaSuggestion.highConfidence');
    if (conf >= 50) return t('areaSuggestion.mediumConfidence');
    return t('areaSuggestion.lowConfidence');
  };

  const displayName = i18n.language === 'zh' ? match.chinese : match.english;
  const regionName = i18n.language === 'zh' ? match.region_chinese : match.region_english;

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Alert
        severity={isCurrentSelection ? "success" : "info"}
        icon={<LocationOn />}
        action={
          !isCurrentSelection && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<CheckCircle />}
                onClick={handleAccept}
              >
                {t('areaSuggestion.accept')}
              </Button>
              <IconButton
                size="small"
                onClick={handleDismiss}
                aria-label={t('areaSuggestion.dismiss')}
              >
                <Cancel />
              </IconButton>
            </Box>
          )
        }
      >
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {isCurrentSelection ?
              t('areaSuggestion.currentSelection') :
              t('areaSuggestion.suggestedArea')
            }
          </Typography>
          <Typography variant="body1">
            {displayName} ({regionName})
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              color={getConfidenceColor(confidence)}
              label={`${confidence}% ${getConfidenceText(confidence)}`}
            />
            {suggestions.length > 1 && (
              <Button
                size="small"
                variant="text"
                endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {t('areaSuggestion.otherSuggestions', { count: suggestions.length - 1 })}
              </Button>
            )}
          </Box>
        </Box>
      </Alert>

      {/* Other suggestions */}
      <Collapse in={isExpanded}>
        <Box sx={{ mt: 1, ml: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {t('areaSuggestion.alternativeSuggestions')}:
          </Typography>
          {suggestions.slice(1, 3).map((suggestion, index) => {
            const suggestionDisplayName = i18n.language === 'zh' ? suggestion.chinese : suggestion.english;
            const suggestionRegionName = i18n.language === 'zh' ? suggestion.region_chinese : suggestion.region_english;

            return (
              <Box key={suggestion._id || index} sx={{ mb: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onAcceptSuggestion({
                    regionId: suggestion.region_id,
                    locationId: suggestion._id,
                    match: suggestion
                  })}
                  sx={{ mr: 1 }}
                >
                  {suggestionDisplayName} ({suggestionRegionName})
                </Button>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${suggestion.score}%`}
                />
              </Box>
            );
          })}
        </Box>
      </Collapse>

      {/* Debug info for development */}
      {showDebugInfo && matchResult.debugInfo && (
        <Collapse in={isExpanded}>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Debug Info:
            </Typography>
            <pre style={{ fontSize: '10px', margin: 0 }}>
              {JSON.stringify(matchResult.debugInfo, null, 2)}
            </pre>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default AreaSuggestion;